import React, { useRef } from "react";
import Skeleton from "react-loading-skeleton";
import { Swiper, SwiperSlide } from "swiper/react";
import { A11y, Navigation, Pagination } from "swiper/modules";
import ChevronsRightIcon from "../../../../shared-components/icons/ChevronsRightIcon";
import ArrowRightIcon from "../../../../shared-components/icons/ArrowRightIcon";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { useIsMobile } from "@/app/redux/features/device/selectors";
import EndsBadge from "@/app/(custom-header)/subscriber-competitions/components/endsBadge";
import ExclusiveBadge from "@/app/(custom-header)/subscriber-competitions/components/exclusiveBadge";
import { useSubscriptionCompetitionsPrizesPageContext } from "../context";
import NrOfTickets from "./nrOfTickets";
import { useIsUserValidated } from "@/app/redux/features/header/selectors";

const UpcomingSubscribers = () => {
  const { category, isCompetitionDetailsLoading, activeCompetition, onViewPlansClick, isAlreadySubscribed } =
    useSubscriptionCompetitionsPrizesPageContext();
  const isUserValidated = useIsUserValidated();

  const isMobile = useIsMobile();

  const swiperRef = useRef<any>(null);
  const BtnSwiperClasses = "relative flex items-center justify-center bg-[#EFEFEF] size-8 rounded-lg";

  const upcomingCompetitions =
    category?.competitions?.filter(competition => competition.CompetitionId !== activeCompetition.CompetitionId) ?? [];

  if (upcomingCompetitions.length === 0) {
    return null;
  }

  return (
    <div className="w-full carousel-container-within-slide-navigation">
      {isCompetitionDetailsLoading ? (
        <Skeleton inline highlightColor="#fff" height={200} containerClassName="w-full bg-[#f1f1f1]" />
      ) : (
        <>
          <div className={"w-full flex items-center gap-4 justify-between"}>
            <div
              className={
                "font-jost text-base font-semibold leading-[22px] bg-gradient-botb bg-clip-text [-webkit-background-clip:text] [-webkit-text-fill-color:transparent]"
              }>
              Upcoming Subscribers Competitions
            </div>
            <div className={"max-lg:hidden lg:flex items-center gap-2"}>
              {/* Prev */}
              <button
                onClick={() => swiperRef.current?.slidePrev()}
                className={`upcoming-prev-btn ${BtnSwiperClasses}`}>
                <ArrowRightIcon className={"size-6 shrink-0 text-[#898994] rotate-180"} />
              </button>

              {/* Next */}
              <button
                onClick={() => swiperRef.current?.slideNext()}
                className={`upcoming-next-btn ${BtnSwiperClasses}`}>
                <ArrowRightIcon className={"size-6 shrink-0 text-[#898994]"} />
              </button>
            </div>
          </div>
          <div className="w-full relative mt-4 max-lg:pb-6">
            <Swiper
              modules={[Navigation, Pagination, A11y]}
              slidesPerView={isMobile ? 1 : 2}
              loop={false}
              spaceBetween={12}
              navigation={{
                nextEl: ".upcoming-next-btn",
                prevEl: ".upcoming-prev-btn",
                disabledClass: "pointer-none !opacity-50",
              }}
              pagination={{
                el: "#upcoming-pagination",
                clickable: true,
              }}
              onSwiper={swiper => (swiperRef.current = swiper)}
              className="w-full">
              {upcomingCompetitions.map((competition, index) => (
                <SwiperSlide key={`banner_${index}`}>
                  <div className="w-full h-full flex flex-col overflow-hidden relative">
                    <div
                      className={
                        "flex w-full min-h-6 lg:min-h-8 justify-center items-center gap-2 bg-[#24339F] p-1 lg:p-2 rounded-t-lg"
                      }>
                      <span
                        className={
                          "uppercase overflow-hidden text-white text-center truncate font-jost text-[11px] font-semibold leading-[14px] lg:text-[13px] lg:font-medium lg:leading-[18px]"
                        }>
                        {competition.Title}
                      </span>
                    </div>

                    <div className="w-full bg-white lg:bg-[#F5F5F5] grid grid-cols-2 lg:grid-cols-[44.594%_1fr] rounded-b-lg">
                      <div className="size-full aspect-[99/61] relative">
                        <EndsBadge label={competition.EndDate} className={"absolute left-2 top-2"} />
                        <ExclusiveBadge className={"absolute bottom-0 right-0"} />
                        <img
                          src={competition.ImageUrl}
                          alt={"competition image url"}
                          className="size-full object-cover rounded-bl-lg"
                          loading="lazy"
                        />
                      </div>
                      <div className="flex flex-col gap-3 items-start justify-between px-4 pt-2 pb-3 lg:pl-4.5 lg:pr-5 lg:pt-3 lg:pb-4">
                        <div
                          className={
                            "w-full text-secondary font-jost text-xs font-medium leading-4 lg:text-sm lg:leading-5 line-clamp-3 lg:line-clamp-2"
                          }>
                          {isUserValidated ? competition.SubtitleTextValidated : competition.SubtitleText}
                        </div>
                        {isAlreadySubscribed && (
                          <div className="w-full flex justify-between max-lg:flex-col max-lg:items-center gap-1">
                            <NrOfTickets compId={competition.CompetitionId} />
                            <button
                              onClick={() => (location.href = competition.PageUrl)}
                              className={
                                "theme-btn theme-btn-primary-outline theme-btn-3xs lg:theme-btn-sm uppercase text-primary gap-1.5 min-w-[137px]"
                              }>
                              Details
                              <ChevronsRightIcon variant={3} className={"size-4 shrink-0"} />
                            </button>
                          </div>
                        )}
                        {!isAlreadySubscribed && (
                          <button
                            onClick={() => onViewPlansClick("sc_details_page")}
                            className={
                              "theme-btn theme-btn-primary-outline theme-btn-3xs lg:theme-btn-sm uppercase text-primary gap-1.5 min-w-[137px]"
                            }>
                            ENTER NOW
                            <ChevronsRightIcon variant={3} className={"size-4 shrink-0"} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>

            <div
              id="upcoming-pagination"
              className="absolute !bottom-0 z-[2] !left-1/2 -translate-x-1/2 flex items-center justify-center gap-2 lg:hidden"></div>
          </div>
        </>
      )}
    </div>
  );
};

export default UpcomingSubscribers;
